import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import Seo, { breadcrumbLd } from "@/components/seo/Seo";
import Jar from "@/components/visual/Jar";
import { BRAND, LAB_REPORT } from "@/content/brand";

const SITE_URL =
  process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

const PROMISES = [
  { title: "Never refined", body: "No refining with heat and chemicals to turn the oil neutral." },
  { title: "Never bleached", body: "Nothing is used to strip out its natural colour." },
  { title: "Never deodorised", body: "It keeps the coconut aroma your recipes expect." },
  { title: "Never blended", body: "No palm, groundnut or any other oil mixed in." },
  { title: "Nothing added", body: "No added colour and no added aroma. One ingredient: coconut." },
];

function SectionHead({ eyebrow, title, sub, dark = false, center = true }) {
  return (
    <div className={center ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
      {eyebrow && <p className={`font-body text-[15px] font-semibold ${dark ? "text-brand-bone/70" : "text-brand-gold"}`}>{eyebrow}</p>}
      <h2 className={`font-display mt-2 text-[clamp(2.2rem,5.5vw,4rem)] leading-[1.02] tracking-display ${dark ? "text-white" : "text-brand-ink"}`}>
        {title}
      </h2>
      {sub && <p className={`mt-5 font-body text-[17px] sm:text-[19px] leading-relaxed ${dark ? "text-brand-bone/70" : "text-ink-muted"}`}>{sub}</p>}
    </div>
  );
}

export default function About() {
  return (
    <>
      <Seo
        title="About Karijeeva: Our Story"
        description="Karijeeva is a brand of Kadle Global Pvt Ltd. We grow coconuts on our family farms in Karnataka, press them cold and test every batch at an independent food testing lab."
        url={`${SITE_URL}/about`}
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "About", path: "/about" }])}
      />

      <div data-testid="page-about">
        {/* Hero */}
        <section className="bg-brand-obsidian text-brand-bone overflow-hidden">
          <div className="max-w-6xl mx-auto px-6 pt-16 sm:pt-24 pb-20 grid lg:grid-cols-[1.4fr_1fr] gap-12 items-center">
            <div>
              <nav aria-label="Breadcrumb" className="font-body text-[13px] text-brand-bone/60">
                <Link to="/" className="hover:underline">Home</Link>
                <span className="mx-2" aria-hidden="true">/</span>
                <span>About</span>
              </nav>
              <p className="mt-6 font-body text-[15px] sm:text-[17px] font-semibold text-brand-bone/80">Our story</p>
              <h1 className="font-display text-white mt-3 text-[clamp(2.6rem,7.5vw,5.75rem)] leading-[0.98] tracking-display" data-testid="about-headline">
                Coconut oil
                <br />
                you can trace.
              </h1>
              <p className="mt-6 max-w-2xl font-body text-[17px] sm:text-[21px] leading-snug text-brand-bone/75">
                Karijeeva is a brand of {BRAND.company}. We grow our own coconuts in Karnataka, press them cold in small batches, and test every batch before it reaches you.
              </p>
            </div>
            <div className="mx-auto w-[170px] sm:w-[210px]">
              <Jar className="w-full h-auto" />
            </div>
          </div>
        </section>

        {/* Why we started */}
        <section className="bg-brand-parchment py-24 sm:py-32">
          <div className="max-w-3xl mx-auto px-6">
            <SectionHead center={false} eyebrow="Why we started" title="Why did we start Karijeeva?" />
            <div className="mt-6 space-y-5 font-body text-[17px] sm:text-[18px] leading-relaxed text-ink-muted">
              <p>
                Coconut oil is in so many Indian kitchens, yet most people can't tell what is really in their jar. Labels rarely say where the coconuts grew. Refined oil and oil mixed with cheaper oils sit on the same shelf as pure oil, and they can look much the same.
              </p>
              <p>
                We wanted a coconut oil we could answer for completely. So we started with the coconuts. We grow them on our family farms, press them ourselves, and send every batch to an independent lab. If you ask where your oil came from or what is in it, we can tell you.
              </p>
            </div>
          </div>
        </section>

        {/* Promises */}
        <section className="bg-brand-parchment-soft py-24 sm:py-32" data-testid="about-promises">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHead eyebrow="What we promise" title="Five things we never do." sub="Refined coconut oil goes through the first three. Ours goes through none of them." />
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {PROMISES.map((p, i) => (
                <div key={p.title} className="rounded-3xl bg-white p-7 min-h-[210px] flex flex-col">
                  <p className="font-display text-[44px] leading-none tracking-display text-brand-gold">{i + 1}</p>
                  <h3 className="font-display text-[22px] tracking-[-0.02em] text-brand-ink mt-4">{p.title}</h3>
                  <p className="font-body text-[15px] leading-relaxed text-ink-muted mt-3">{p.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 text-center">
              <Link to="/cold-press-process" className="inline-flex items-center gap-1 font-body text-[17px] text-brand-gold hover:underline">
                How we make it <ChevronRight className="w-4 h-4" />
              </Link>
            </p>
          </div>
        </section>

        {/* Where we grow */}
        <section className="bg-brand-obsidian text-brand-bone py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6">
            <SectionHead
              dark
              eyebrow="Where we grow"
              title="Two Karnataka coconut lands. Both ours."
              sub="We don't buy oil and relabel it. We make only as much as our farms grow, which is why some batches sell out."
            />
            <div className="mt-14 grid md:grid-cols-2 gap-4">
              {BRAND.origins.map((o) => (
                <div key={o.key} className="rounded-3xl bg-brand-obsidian-soft p-10 min-h-[300px] flex flex-col">
                  <p className="font-body text-[13px] font-semibold text-brand-bone/60">{o.share}</p>
                  <h3 className="font-display text-white text-[40px] leading-none tracking-heading mt-3">{o.name}</h3>
                  <p className="font-body text-brand-bone/60 mt-2">{o.region}</p>
                  <p className="font-body text-[17px] leading-relaxed text-brand-bone/80 mt-auto pt-10">{o.line}</p>
                </div>
              ))}
            </div>
            <p className="mt-10 text-center">
              <Link to="/the-farm" className="inline-flex items-center gap-1 font-body text-[17px] text-white hover:underline">
                Visit our farms <ChevronRight className="w-4 h-4" />
              </Link>
            </p>
          </div>
        </section>

        {/* How we test */}
        <section className="bg-brand-parchment py-24 sm:py-32">
          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
            <SectionHead
              center={false}
              eyebrow="How we test"
              title="Every batch goes to the lab."
              sub={`Before any batch is packed, a sample goes to an independent food testing lab. We have tested ${BRAND.batchesTested} batches so far, and we publish our latest report in full. Every jar carries a batch number, so you can ask us for the report on yours.`}
            />
            <div className="rounded-3xl bg-white p-8 sm:p-10">
              <p className="font-body text-[13px] text-ink-muted">Latest report {LAB_REPORT.ref}</p>
              <p className="font-display text-[64px] leading-none tracking-display text-brand-ink mt-4">{BRAND.batchesTested}</p>
              <p className="font-display text-[20px] text-brand-ink mt-2">batches lab tested</p>
              <p className="font-body text-[15px] text-ink-muted mt-6">FSSAI licence {BRAND.fssai}</p>
              <Link to="/lab-reports" className="mt-6 inline-flex items-center gap-1 font-body text-[15px] text-brand-gold hover:underline">
                See the lab report <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-brand-obsidian text-center py-28 sm:py-32">
          <div className="max-w-3xl mx-auto px-6">
            <h2 className="font-display text-white text-[clamp(2.2rem,5.5vw,4rem)] leading-[1] tracking-display">
              Switch to coconut oil you can trace to the farm.
            </h2>
            <p className="mt-6 font-body text-[19px] text-brand-bone/70">Delivered across India. Free delivery on orders above ₹998.</p>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
              <Link to="/products" className="inline-flex items-center rounded-control bg-brand-gold hover:bg-brand-gold-deep text-white font-body text-[17px] px-7 py-3.5 transition-colors">
                Shop Karijeeva
              </Link>
              <Link to="/lab-reports" className="inline-flex items-center gap-1 font-body text-[17px] text-white hover:underline">
                See the lab report <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <ul className="mt-12 flex flex-wrap justify-center gap-3">
              {[
                { to: "/cold-pressed-vs-refined-coconut-oil", label: "Cold pressed vs refined" },
                { to: "/how-to-check-coconut-oil-purity", label: "How to check purity" },
                { to: "/faqs", label: "Questions people ask" },
              ].map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="inline-flex rounded-control bg-brand-obsidian-soft px-4 py-2 font-body text-[14px] text-brand-bone hover:text-white">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </>
  );
}
